import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EyeOff,
  ListChecks,
  Plus,
  Edit2,
  Trash2,
  X,
  Loader2,
  Check,
  ShieldAlert,
  Activity,
  Wrench,
} from 'lucide-react';
import { api } from '@/shared/api';

interface PIIRule {
  id: string;
  name: string;
  label: string;
  description: string | null;
  pattern_type: 'regex' | 'llm_hint';
  pattern: string;
  replacement: string;
  severity: 'low' | 'medium' | 'high';
  enabled: boolean;
  is_system: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface PrivacyStats {
  total_detections: number;
  active_detections: number;
  false_positives: number;
  scanned_documents: number;
  total_documents: number;
  coverage_pct: number;
  rules_total: number;
  rules_enabled: number;
  by_label: { label: string; count: number }[];
}

type View = 'rules' | 'stats';

const VIEW_TITLES: Record<View, { label: string; latin: string; icon: any }> = {
  rules: { label: 'Aturan PII',         latin: 'REGULAE',   icon: ListChecks },
  stats: { label: 'Statistik & Cakupan', latin: 'METRICA',   icon: Activity },
};

const SEV_COLOR: Record<string, string> = {
  high:   'text-crimson border-crimson bg-crimson/5',
  medium: 'text-brass border-brass bg-brass-soft/40',
  low:    'text-ink-2 border-ink-line bg-paper-2/40',
};

export default function PrivacyPage() {
  const [searchParams] = useSearchParams();
  const view = (searchParams.get('view') as View) || 'rules';
  const t = VIEW_TITLES[view] || VIEW_TITLES.rules;
  const Icon = t.icon;

  return (
    <div>
      <div className="mb-4 pb-2 border-b-2 border-ink flex items-baseline gap-3 flex-wrap">
        <Icon className="w-5 h-5 text-brass self-center" />
        <h1 className="font-display font-bold text-[22px] leading-tight tracking-tight">{t.label}</h1>
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3">— Privacy · {t.latin}</span>
        <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-ink-3">PII Oversight</span>
      </div>

      <div className="p-3 mb-5 border border-maroon/30 bg-maroon/5 flex items-start gap-2 font-mono text-[10px] uppercase tracking-wider text-maroon">
        <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Modul Privacy — aturan PII di sini dipakai oleh pipeline scan untuk men-mask data sensitif sebelum BAP diekspor / dipakai untuk pelatihan model.
        </span>
      </div>

      {view === 'rules' && <RulesView />}
      {view === 'stats' && <StatsView />}
    </div>
  );
}

// ============================================================
// RULES VIEW
// ============================================================

function RulesView() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PIIRule | null>(null);
  const [creating, setCreating] = useState(false);

  const { data: rules, isLoading } = useQuery<PIIRule[]>({
    queryKey: ['privacy-rules'],
    queryFn: () => api('/api/v1/privacy/rules'),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api(`/api/v1/privacy/rules/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['privacy-rules'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api(`/api/v1/privacy/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['privacy-rules'] }),
    onError: (e: any) => alert(String(e?.message || e)),
  });

  return (
    <section>
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <p className="font-mono text-[11px] text-ink-2">
          Daftar aturan deteksi PII. Rule sistem bisa diaktifkan/dinonaktifkan + diedit; hanya rule custom yang bisa dihapus.
        </p>
        <button
          onClick={() => setCreating(true)}
          className="ml-auto px-3 h-9 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5"
        >
          <Plus className="w-3.5 h-3.5" />
          Tambah Rule
        </button>
      </div>

      {isLoading && <p className="font-mono text-xs text-ink-2">Memuat...</p>}

      <div className="border border-ink-line overflow-x-auto">
        <div className="grid grid-cols-[1fr_90px_100px_2fr_100px_70px_100px] min-w-[860px] gap-2 px-3 py-2 border-b border-ink bg-paper-2/60 font-mono text-[9px] uppercase tracking-widest text-ink-3">
          <span>Label / Name</span>
          <span>Type</span>
          <span>Severity</span>
          <span>Pattern</span>
          <span>Replacement</span>
          <span className="text-center">Enabled</span>
          <span className="text-right">Aksi</span>
        </div>
        {(rules || []).map((r) => (
          <div
            key={r.id}
            className={`grid grid-cols-[1fr_90px_100px_2fr_100px_70px_100px] min-w-[860px] gap-2 px-3 py-2.5 border-b border-dashed border-ink-line/60 last:border-b-0 font-mono text-[11px] items-center ${
              !r.enabled ? 'opacity-50' : ''
            }`}
          >
            <div className="min-w-0">
              <div className="font-bold text-ink truncate" title={r.description || ''}>{r.label}</div>
              <div className="text-ink-3 text-[9px] truncate">{r.name}{r.is_system && ' · sistem'}</div>
            </div>
            <span className={r.pattern_type === 'regex' ? 'text-bluestamp font-bold' : 'text-olive font-bold'}>
              {r.pattern_type === 'regex' ? 'REGEX' : 'LLM'}
            </span>
            <span className={`px-2 py-0.5 border text-center font-bold text-[10px] uppercase ${SEV_COLOR[r.severity] || ''}`}>
              {r.severity}
            </span>
            <code className="text-ink-2 font-mono text-[10px] truncate" title={r.pattern}>{r.pattern}</code>
            <code className="text-brass font-mono text-[10px] truncate" title={r.replacement}>{r.replacement}</code>
            <div className="flex justify-center">
              <button
                onClick={() => toggleMut.mutate({ id: r.id, enabled: !r.enabled })}
                disabled={toggleMut.isPending}
                className={`w-9 h-5 relative transition ${r.enabled ? 'bg-olive' : 'bg-ink-line'}`}
                title={r.enabled ? 'Klik untuk nonaktifkan' : 'Klik untuk aktifkan'}
              >
                <span className={`absolute top-0.5 w-4 h-4 bg-paper transition-transform ${r.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="flex justify-end gap-1">
              <button
                onClick={() => setEditing(r)}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-brass hover:text-brass transition"
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </button>
              <button
                onClick={() => {
                  if (r.is_system) { alert('Rule bawaan sistem tidak bisa dihapus — gunakan toggle Enabled.'); return; }
                  if (confirm(`Hapus rule ${r.label}?`)) deleteMut.mutate(r.id);
                }}
                disabled={r.is_system}
                className="w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition disabled:opacity-30 disabled:cursor-not-allowed"
                title={r.is_system ? 'Rule sistem' : 'Hapus'}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        {rules && rules.length === 0 && (
          <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">Belum ada rule PII.</p>
        )}
      </div>

      {(editing || creating) && (
        <RuleModal
          rule={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => {
            setEditing(null);
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['privacy-rules'] });
          }}
        />
      )}
    </section>
  );
}

function RuleModal({ rule, onClose, onSaved }: { rule: PIIRule | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(rule?.name || '');
  const [label, setLabel] = useState(rule?.label || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [patternType, setPatternType] = useState<'regex' | 'llm_hint'>(rule?.pattern_type || 'regex');
  const [pattern, setPattern] = useState(rule?.pattern || '');
  const [replacement, setReplacement] = useState(rule?.replacement || '');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>(rule?.severity || 'medium');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const body: any = {
        label,
        description: description || null,
        pattern_type: patternType,
        pattern,
        replacement,
        severity,
        enabled,
      };
      if (rule) {
        return api(`/api/v1/privacy/rules/${rule.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      }
      body.name = name;
      return api('/api/v1/privacy/rules', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: onSaved,
    onError: (e: any) => setError(String(e?.message || e)),
  });

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[100] grid place-items-center p-8" onClick={onClose}>
      <div className="bg-paper border-2 border-ink max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-3 border-b-2 border-ink bg-paper-2/40">
          <EyeOff className="w-4 h-4 text-brass" />
          <h3 className="font-display font-bold text-[16px]">{rule ? `Edit Rule · ${rule.label}` : 'Tambah Rule PII Baru'}</h3>
          <button onClick={onClose} className="ml-auto w-7 h-7 grid place-items-center border border-ink-line hover:border-crimson hover:text-crimson transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); saveMut.mutate(); }} className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 font-mono text-[11px]">
          <Field label="Nama (slug)" required>
            <input
              type="text" required value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!rule}
              placeholder="nip_pegawai"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none disabled:opacity-60"
            />
          </Field>
          <Field label="Label" required>
            <input
              type="text" required value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="NIP Pegawai"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Deskripsi" cols={2}>
            <input
              type="text" value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Deskripsi singkat data yang ingin di-mask"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            />
          </Field>
          <Field label="Tipe Pattern" required>
            <select
              value={patternType}
              onChange={(e) => setPatternType(e.target.value as any)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            >
              <option value="regex">Regex</option>
              <option value="llm_hint">LLM Hint (natural language)</option>
            </select>
          </Field>
          <Field label="Severity" required>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as any)}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </Field>
          <Field label={patternType === 'regex' ? 'Regex Pattern' : 'LLM Hint Phrase'} required cols={2}>
            <input
              type="text" required value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder={patternType === 'regex' ? '\\b\\d{18}\\b' : 'Nomor identifikasi internal pegawai yang muncul di BAP'}
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none font-mono"
            />
          </Field>
          <Field label="Replacement (placeholder masking)" required cols={2}>
            <input
              type="text" required value={replacement}
              onChange={(e) => setReplacement(e.target.value)}
              placeholder="[NIP_DISAMARKAN]"
              className="w-full px-2 py-1.5 border border-ink-line bg-paper focus:border-brass outline-none font-mono"
            />
          </Field>
          <div className="col-span-2 flex items-center gap-2 pt-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-3.5 h-3.5 accent-brass" />
              <span className="uppercase tracking-wider">Aktif (rule dipakai oleh PII scan)</span>
            </label>
          </div>

          {error && (
            <div className="col-span-2 p-3 border-2 border-crimson bg-crimson/5 text-crimson text-[10px]">
              ⚠ {error}
            </div>
          )}

          <div className="col-span-2 flex gap-2 pt-4 border-t border-dashed border-ink-line">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-ink-line text-ink-2 hover:border-ink font-mono text-[11px] uppercase tracking-wider">
              Batal
            </button>
            <button type="submit" disabled={saveMut.isPending} className="ml-auto px-5 py-2 bg-ink text-paper hover:bg-brass transition font-mono text-[11px] uppercase tracking-wider font-semibold flex items-center gap-2 disabled:opacity-50">
              {saveMut.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// STATS VIEW
// ============================================================

function StatsView() {
  const { data: stats, isLoading } = useQuery<PrivacyStats>({
    queryKey: ['privacy-stats'],
    queryFn: () => api('/api/v1/privacy/stats'),
    refetchInterval: 30000,
  });

  if (isLoading) return <p className="font-mono text-xs text-ink-2">Memuat...</p>;
  if (!stats) return <p className="font-mono text-xs text-ink-3 italic">Belum ada data.</p>;

  return (
    <section>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Total Deteksi" value={stats.total_detections} />
        <Stat label="Aktif (bukan FP)" value={stats.active_detections} accent />
        <Stat label="False Positive" value={stats.false_positives} muted />
        <Stat label="Rules Aktif" value={`${stats.rules_enabled}/${stats.rules_total}`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-ink-line p-4">
          <h3 className="font-display font-bold text-[14px] mb-3 pb-2 border-b border-ink-line/60">Cakupan Pemindaian</h3>
          <div className="font-display font-bold text-[36px] tabular-nums text-brass-2">
            {stats.coverage_pct.toFixed(1)}<span className="text-[20px] text-ink-3">%</span>
          </div>
          <p className="font-mono text-[11px] text-ink-2 mt-2">
            {stats.scanned_documents} dari {stats.total_documents} BAP sudah di-scan PII.
          </p>
          <div className="mt-3 h-2 bg-paper-2 border border-ink-line">
            <div
              className="h-full bg-brass"
              style={{ width: `${Math.min(100, stats.coverage_pct)}%` }}
            />
          </div>
        </div>

        <div className="border border-ink-line p-4">
          <h3 className="font-display font-bold text-[14px] mb-3 pb-2 border-b border-ink-line/60">Breakdown per Label</h3>
          {stats.by_label.length === 0 ? (
            <p className="font-mono text-[11px] text-ink-3 italic">Belum ada deteksi.</p>
          ) : (
            <ul className="space-y-2">
              {stats.by_label.slice(0, 10).map((b) => {
                const max = stats.by_label[0].count || 1;
                const pct = (100 * b.count) / max;
                return (
                  <li key={b.label} className="grid grid-cols-[100px_1fr_auto] gap-3 items-center font-mono text-[11px]">
                    <span className="text-ink-2 truncate">{b.label}</span>
                    <div className="h-2 bg-paper-2 border border-ink-line">
                      <div className="h-full bg-bluestamp" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-ink font-bold tabular-nums">{b.count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="p-3 border border-ink-line bg-paper-2/30 flex items-start gap-2 font-mono text-[10px] text-ink-2">
        <Wrench className="w-4 h-4 mt-0.5 shrink-0 text-brass" />
        <span>
          Wiring PII scan service ke tabel <code className="text-brass">pii_rules</code> akan dilakukan di Phase berikutnya
          (hari ini scan masih pakai daftar regex hardcoded). Editor di tab Aturan sudah siap menyimpan rule custom.
        </span>
      </div>
    </section>
  );
}

function Stat({ label, value, accent, muted }: { label: string; value: number | string; accent?: boolean; muted?: boolean }) {
  return (
    <div className="border border-ink-line p-4">
      <div className={`font-display font-bold text-[28px] tabular-nums ${accent ? 'text-olive' : muted ? 'text-ink-3' : 'text-brass-2'}`}>
        {value}
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mt-1">{label}</div>
    </div>
  );
}

function Field({ label, children, required, cols }: { label: string; children: React.ReactNode; required?: boolean; cols?: number }) {
  return (
    <div className={cols === 2 ? 'col-span-2' : ''}>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1">
        {label} {required && <span className="text-crimson">*</span>}
      </label>
      {children}
    </div>
  );
}
